import React, { useEffect, useRef, useState } from "react";
import { FiSliders } from "react-icons/fi";
import "./PageToolbar.css";

export interface FilterOption {
  value: string;
  label: string;
}

interface PageFilterDropdownProps {
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
  allLabel?: string;
  align?: "left" | "right";
}

const PageFilterDropdown: React.FC<PageFilterDropdownProps> = ({
  value,
  options,
  onChange,
  allLabel = "All",
  align = "right",
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = Boolean(value);
  const activeLabel =
    options.find((o) => o.value === value)?.label ?? allLabel;

  const handleSelect = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div className="page-filter" ref={ref}>
      <button
        type="button"
        className={`page-filter-btn${isActive ? " is-active" : ""}${open ? " is-open" : ""}`}
        onClick={() => setOpen((s) => !s)}
        aria-expanded={open}
        aria-haspopup="listbox"
        title={isActive ? `Filtered: ${activeLabel}` : "Filter"}
      >
        <FiSliders aria-hidden />
        {isActive && <span className="page-filter-dot" aria-hidden />}
      </button>

      {open && (
        <div
          className={`page-filter-menu page-filter-menu--${align}`}
          role="listbox"
          aria-label="Filter options"
        >
          <button
            type="button"
            role="option"
            aria-selected={value === ""}
            className={`page-filter-option${value === "" ? " is-selected" : ""}`}
            onClick={() => handleSelect("")}
          >
            {allLabel}
          </button>
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={value === opt.value}
              className={`page-filter-option${value === opt.value ? " is-selected" : ""}`}
              onClick={() => handleSelect(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PageFilterDropdown;
